import { useEffect, useRef, useState } from 'react';

const parseNumericValue = (value) => {
  const raw = String(value ?? '').replace(/,/g, '');
  const match = raw.match(/-?\d*\.?\d+/);
  if (!match) return null;

  const numeric = Number.parseFloat(match[0]);
  if (Number.isNaN(numeric)) return null;

  const decimals = match[0].includes('.') ? match[0].split('.')[1].length : 0;
  return { numeric, decimals };
};

const formatNumber = (value, decimals) => {
  if (decimals > 0) {
    return value.toFixed(decimals);
  }
  return Math.round(value).toString();
};

const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export default function CountUpValue({ value, className, duration = 1000 }) {
  const elementRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const [displayValue, setDisplayValue] = useState(() => {
    const parsed = parseNumericValue(value);
    return parsed ? '0' : String(value ?? '');
  });

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const parsed = parseNumericValue(value);
    if (!parsed) {
      setDisplayValue(String(value ?? ''));
      return undefined;
    }

    hasAnimatedRef.current = false;
    let rafId = 0;

    const startAnimation = () => {
      if (prefersReducedMotion()) {
        setDisplayValue(String(value ?? ''));
        hasAnimatedRef.current = true;
        return;
      }

      const startTime = performance.now();
      const { numeric, decimals } = parsed;
      setDisplayValue(formatNumber(0, decimals));

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const currentValue = numeric * progress;
        setDisplayValue(formatNumber(currentValue, decimals));

        if (progress < 1) {
          rafId = requestAnimationFrame(step);
        } else {
          setDisplayValue(String(value ?? ''));
          hasAnimatedRef.current = true;
        }
      };

      rafId = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            startAnimation();
          }
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(element);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [duration, value]);

  return (
    <div ref={elementRef} className={className}>
      {displayValue}
    </div>
  );
}
